import { ComponentProps, useState } from 'react';
import { FieldErrors, SubmitHandler, UseFormRegister, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../../lib/utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { PasswordRequirements } from '../../../molecules/password-requirements/password-requirements';
import { createSubmitHandler } from '../handle-submit';
import { PASSWORD_PATTERN } from '../password-validation';

type Inputs = {
  confirmationCode: string;
  password: string;
  confirmPassword: string;
};

const ConfirmationCodeField = ({ variant, ...props }: ComponentProps<typeof Input>) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="confirmationCode">{t('auth.confirmForgotPassword.codeLabel')}</Label>
      <Input type="text" variant={variant} {...props} />
    </div>
  );
};

const PasswordField = ({
  variant,
  label,
  ...props
}: ComponentProps<typeof Input> & {
  readonly variant?: 'default' | 'error';
  readonly label: string;
  readonly showRequirements?: boolean;
}) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const passwordInputType = showPassword ? 'text' : 'password';

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={props.name}>{t(label)}</Label>
      <Input
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {showPassword ? t('common.buttons.hide') : t('common.buttons.show')}
          </Text>
        }
        type={passwordInputType}
        variant={variant}
        {...props}
      />
    </div>
  );
};

const CodeFieldWithResend = ({
  variant,
  register,
  onResendCode,
}: {
  readonly variant?: 'default' | 'error';
  readonly register: ReturnType<UseFormRegister<Inputs>>;
  readonly onResendCode?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <ConfirmationCodeField variant={variant} {...register} />
      {onResendCode && (
        <Text className="text-sm text-gray-600">
          {t('auth.confirmForgotPassword.resendCode.didntReceive')}{' '}
          <button
            className="underline cursor-pointer text-blue-600 hover:text-blue-800"
            onClick={onResendCode}
            type="button"
          >
            {t('auth.confirmForgotPassword.resendCode.resendButton')}
          </button>
        </Text>
      )}
    </div>
  );
};

const FormFields = ({
  errors,
  register,
  email,
  onResendCode,
  watchPassword,
}: {
  readonly errors: FieldErrors<Inputs>;
  readonly register: UseFormRegister<Inputs>;
  readonly email: string;
  readonly onResendCode?: () => Promise<void>;
  readonly watchPassword: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <Header level="h1">{t('auth.confirmForgotPassword.title')}</Header>
      <Text>
        <Trans components={{ strong: <strong /> }} i18nKey="auth.confirmForgotPassword.description" />
      </Text>
      <CodeFieldWithResend
        onResendCode={onResendCode}
        register={register('confirmationCode', { required: t('auth.common.errors.confirmationCodeMissing') })}
        variant={errors.confirmationCode ? 'error' : 'default'}
      />
      <PasswordField
        label={t('auth.confirmForgotPassword.newPasswordLabel')}
        variant={errors.password ? 'error' : 'default'}
        {...register('password', {
          required: t('auth.common.errors.missingPasswordFields'),
          pattern: {
            value: PASSWORD_PATTERN,
            message: t('auth.common.errors.passwordFormatDescription'),
          },
        })}
      />
      <PasswordField
        label="auth.confirmForgotPassword.repeatPasswordLabel"
        variant={errors.confirmPassword ? 'error' : 'default'}
        {...register('confirmPassword', {
          required: t('auth.common.errors.passwordConfirmationMissing'),
          validate: (value: string) => value === watchPassword || t('auth.common.errors.passwordsDoNotMatch'),
        })}
      />
      <PasswordRequirements />
    </div>
  );
};

const ConfirmForgotPasswordForm = ({
  onSubmit,
  email,
  onResendCode,
}: {
  readonly onSubmit: SubmitHandler<Inputs>;
  readonly email: string;
  readonly onResendCode?: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();

  const watchPassword = watch('password');
  const submitHandler = handleSubmit(createSubmitHandler({ onSubmit, setError }));
  const formErrors = collectFormErrors(errors, t);
  const handleResendCodeClick = async () => {
    try {
      onResendCode && (await onResendCode());
    } catch (error) {
      if (error instanceof Error) {
        setError('root', { message: error.message });
        return;
      }

      setError('root', { message: 'Something went wrong' });
    }
  };

  return (
    <form onSubmit={submitHandler}>
      <FormFields
        email={email}
        errors={errors}
        onResendCode={onResendCode && handleResendCodeClick}
        register={register}
        watchPassword={watchPassword}
      />
      <div className="flex flex-col gap-7.5 mt-7.5">
        <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
        <Button className="w-full" loading={isSubmitting} type="submit">
          {t('auth.confirmForgotPassword.resetPasswordButton')}
        </Button>
      </div>
    </form>
  );
};

export { ConfirmForgotPasswordForm };
